import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './hooks/useAuth'
import Layout from './components/Layout'
import PublicLayout from './components/PublicLayout'
import LoadingSpinner from './components/LoadingSpinner'
import RoleProtectedRoute from './components/RoleProtectedRoute'

// Pages
import HomePage from './pages/HomePage'
import NewLandingPage from './pages/NewLandingPage'
import LoginPage from './pages/LoginPage'
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage'
import ResetPasswordPage from './pages/auth/ResetPasswordPage'
import ForcedChangePasswordPage from './pages/ForcedChangePasswordPage'
import FaqPage from './pages/FaqPage'
import MainLayout from './layouts/MainLayout'
import RegisterPage from './pages/RegisterPage'
import DashboardPage from './pages/dashboard/DashboardPage'
import CasesPage from './pages/cases/CasesPage'
import CaseDetailPage from './pages/cases/CaseDetailPage'
import CreateCasePage from './pages/cases/CreateCasePage'
import ProvidersPage from './pages/providers/ProvidersPage'
import ProfilePage from './pages/profile/ProfilePage'
import AdminDashboardPage from './pages/admin/AdminDashboardPage'
import AdminIntegrationsPage from './pages/admin/AdminIntegrationsPage'
import PackageManagementPage from './pages/admin/PackageManagementPage'
import BookingApprovalPage from './pages/admin/BookingApprovalPage'
import AccountManagementPage from './pages/admin/AccountManagementPage'
import PaymentVerificationPage from './pages/admin/PaymentVerificationPage'
import VerifiedPaymentsPage from './pages/admin/VerifiedPaymentsPage'
import ProviderOverviewPage from './pages/admin/providers/ProviderOverviewPage'
import PendingApprovalPage from './pages/admin/providers/PendingApprovalPage'
import ActiveProvidersPage from './pages/admin/providers/ActiveProvidersPage'
import ProviderAnalyticsPage from './pages/admin/providers/ProviderAnalyticsPage'
import ProviderSettingsPage from './pages/admin/providers/ProviderSettingsPage'
import ProviderDocumentVerificationPage from './pages/admin/providers/ProviderDocumentVerificationPage'
import ProviderDetailsPage from './pages/admin/providers/ProviderDetailsPage'
import ActiveProviderDetailsPage from './pages/admin/providers/ActiveProviderDetailsPage'
import UserManagementPage from './pages/admin/UserManagementPage'
import UserDetailPage from './pages/admin/UserDetailPage'
import FinanceVABalancePage from './pages/finance/FinanceVABalancePage'
import UserDocumentsSection from './components/admin/UserDocumentsSection'
import ProviderDashboardPage from './pages/provider-dashboard/ProviderDashboardPage'
import ProviderProfilePage from './pages/provider-dashboard/ProviderProfilePage'
import ProviderSchedulePage from './pages/provider-dashboard/ProviderSchedulePage'
import ProviderAssetsPage from './pages/provider-dashboard/ProviderAssetsPage'
import AssetManagementPage from './pages/provider-dashboard/AssetManagementPage'
import AssetsRouter from './pages/provider-dashboard/AssetsRouter'
import AmbulanceAssetManagementPage from './pages/provider-dashboard/AmbulanceAssetManagementPage'
import CemeteryAssetManagementPage from './pages/provider-dashboard/CemeteryAssetManagementPage'
import ProviderBookingsPage from './pages/provider-dashboard/ProviderBookingsPage'
import ProviderInfaqPage from './pages/provider-dashboard/ProviderInfaqPage'
import MaintenanceInfaqDetailPage from './pages/provider-dashboard/MaintenanceInfaqDetailPage'
import AmbulanceBookingPage from './pages/bookings/AmbulanceBookingPage'
import AmbulanceBookingsPage from './pages/bookings/AmbulanceBookingsPage'
import AmbulanceBookingDetailsPage from './pages/bookings/AmbulanceBookingDetailsPage'
import AmbulanceBookingPaymentPage from './pages/bookings/AmbulanceBookingPaymentPage'
import BurialPlotBookingsPage from './pages/bookings/BurialPlotBookingsPage'
import BurialPlotBookingPage from './pages/bookings/BurialPlotBookingPage'
import BurialPlotSelectionPage from './pages/bookings/BurialPlotSelectionPage'
import BurialPlotPaymentPage from './pages/bookings/BurialPlotPaymentPage'
import BurialPlotBookingDetailsPage from './pages/bookings/BurialPlotBookingDetailsPage'
import NotFoundPage from './pages/NotFoundPage'
import SouvenirProductsPage from './pages/SouvenirProductsPage'
import SouvenirProductDetailPage from './pages/SouvenirProductDetailPage'
import SouvenirVendorDashboard from './pages/vendor-dashboard/SouvenirVendorDashboard'
import VendorProductsPage from './pages/vendor-dashboard/VendorProductsPage'
import VendorOrdersPage from './pages/vendor-dashboard/VendorOrdersPage'
import VendorProfilePage from './pages/vendor-dashboard/VendorProfilePage'
import NewProductPage from './pages/vendor-dashboard/NewProductPage'
import ProductDetailPage from './pages/vendor-dashboard/ProductDetailPage'
import EditProductPage from './pages/vendor-dashboard/EditProductPage'
import InfaqPage from './pages/infaq/InfaqPage'
import CampaignDetailPage from './pages/infaq/CampaignDetailPage'
import MaintenanceCampaignDetailPage from './pages/infaq/MaintenanceCampaignDetailPage'
import InfaqCampaignsPage from './pages/admin/infaq/InfaqCampaignsPage'
import InfaqCampaignFormPage from './pages/admin/infaq/InfaqCampaignFormPage'
import InfaqAllocateFundsFormPage from './pages/admin/infaq/InfaqAllocateFundsFormPage'
import PromoCodesPage from './pages/admin/promos/PromoCodesPage'
import InfaqDonationsPage from './pages/admin/infaq/InfaqDonationsPage'
import InfaqAnalyticsPage from './pages/admin/infaq/InfaqAnalyticsPage'
import MaintenanceInfaqApprovalPage from './pages/admin/infaq/MaintenanceInfaqApprovalPage'
import WalletPage from './pages/wallet/WalletPage'
import FamilyTreePage from './components/family-tree/FamilyTreePage'
import MyWillPage from './pages/will/MyWillPage'
import InheritanceClaimPage from './pages/claims/InheritanceClaimPage'
import TermsOfServicePage from './pages/TermsOfServicePage'
import AboutPage from './pages/AboutPage'
import PrivacyPolicyPage from './pages/PrivacyPolicyPage'
import InheritanceClaimsPage from './pages/admin/InheritanceClaimsPage'
import InheritanceClaimDetailPage from './pages/admin/InheritanceClaimDetailPage'
import PayoutsPage from './pages/admin/PayoutsPage'
import PayoutBatchDetailPage from './pages/admin/PayoutBatchDetailPage'
import CartPage from './pages/souvenir/CartPage'
import CheckoutPage from './pages/souvenir/CheckoutPage'
import MyOrdersPage from './pages/souvenir/MyOrdersPage'
import OrderDetailPage from './pages/souvenir/OrderDetailPage'
import { useLocation } from 'react-router-dom'

// Helper function to get dashboard path based on user role
const getDashboardPath = (user: any) => {
  if (!user) return '/dashboard'

  switch (user.role) {
    case 'PROVIDER':
    case 'AMBULANCE_PROVIDER':
    case 'USTADZ':
    case 'CEMETERY_PROVIDER':
    case 'VENDOR':
    case 'OPERATOR_TRAINING':
    case 'OPERATOR_OPERATION':
      return '/provider-dashboard'
    case 'ADMIN':
      return '/admin'
    case 'FINANCE':
      return '/finance'
    case 'FAMILY':
    default:
      return '/dashboard'
  }
}

function App() {
  const { user, isLoading } = useAuth()
  const location = useLocation()

  // Path to return to after a login that was triggered by a protected-route
  // bounce (RoleProtectedRoute passes the intended path via router state).
  // Same-origin paths only — never redirect off-site. Temp-credentialed users
  // (mustChangePassword) are sent to the forced password-change screen.
  const getPostLoginPath = () => {
    if (user?.mustChangePassword) return '/first-login'
    const from = (location.state as { from?: unknown } | null)?.from
    return typeof from === 'string' && from.startsWith('/') && !from.startsWith('//')
      ? from
      : getDashboardPath(user)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<MainLayout />}>
          <Route index element={<NewLandingPage />} />
          <Route path="faq" element={<FaqPage />} />
        </Route>
        <Route path="/login" element={!user ? <LoginPage /> : <Navigate to={getPostLoginPath()} />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/first-login" element={user ? <ForcedChangePasswordPage /> : <Navigate to="/login" replace />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/claims/inheritance" element={<InheritanceClaimPage />} />
        <Route path="/register" element={!user ? <RegisterPage /> : <Navigate to={getPostLoginPath()} />} />

        {/* Products - Public Routes with PublicLayout (no login required) */}
        <Route element={<PublicLayout />}>
          <Route path="/products" element={<SouvenirProductsPage />} />
          <Route path="/products/:category" element={<SouvenirProductsPage />} />
          <Route path="/products/:category/:slug" element={<SouvenirProductDetailPage />} />
          <Route path="/souvenir-products" element={<Navigate to="/products" replace />} />
          <Route path="/souvenir-products/:id" element={<Navigate to="/products" replace />} />
        </Route>

        {/* Infaq/Donation Campaigns - Public Routes */}
        <Route path="/infaq" element={<InfaqPage />} />
        <Route path="/infaq/campaign/:slug" element={<CampaignDetailPage />} />
        <Route path="/infaq/maintenance/:slug" element={<MaintenanceCampaignDetailPage />} />

        {/* Protected routes */}
        <Route
          path="/"
          element={user ? <Layout /> : <Navigate to="/login" state={{ from: location.pathname + location.search }} />}
        >
          {/* Family and Admin routes */}
          <Route path="dashboard" element={
            <RoleProtectedRoute allowedRoles={['FAMILY', 'ADMIN']}>
              <DashboardPage />
            </RoleProtectedRoute>
          } />
          <Route path="cases" element={
            <RoleProtectedRoute allowedRoles={['FAMILY', 'ADMIN']}>
              <CasesPage />
            </RoleProtectedRoute>
          } />
          <Route path="cases/new" element={
            <RoleProtectedRoute allowedRoles={['FAMILY', 'ADMIN']}>
              <CreateCasePage />
            </RoleProtectedRoute>
          } />
          <Route path="cases/:caseNumber" element={
            <RoleProtectedRoute allowedRoles={['FAMILY', 'ADMIN']}>
              <CaseDetailPage />
            </RoleProtectedRoute>
          } />
          <Route path="bookings" element={
            <RoleProtectedRoute allowedRoles={['FAMILY', 'ADMIN']}>
              <AmbulanceBookingsPage />
            </RoleProtectedRoute>
          } />
          <Route path="bookings/ambulance" element={
            <RoleProtectedRoute allowedRoles={['FAMILY', 'ADMIN']}>
              <AmbulanceBookingsPage />
            </RoleProtectedRoute>
          } />
          <Route path="bookings/burial-plot/bookings" element={
            <RoleProtectedRoute allowedRoles={['FAMILY', 'ADMIN']}>
              <BurialPlotBookingsPage />
            </RoleProtectedRoute>
          } />
          <Route path="bookings/ambulance/new" element={
            <RoleProtectedRoute allowedRoles={['FAMILY', 'ADMIN']}>
              <AmbulanceBookingPage />
            </RoleProtectedRoute>
          } />
          <Route path="bookings/burial-plot" element={
            <RoleProtectedRoute allowedRoles={['FAMILY', 'ADMIN']}>
              <BurialPlotBookingPage />
            </RoleProtectedRoute>
          } />
          <Route path="bookings/burial-plot/select-plot" element={
            <RoleProtectedRoute allowedRoles={['FAMILY', 'ADMIN']}>
              <BurialPlotSelectionPage />
            </RoleProtectedRoute>
          } />
          <Route path="bookings/burial-plot/payment/:bookingId" element={
            <RoleProtectedRoute allowedRoles={['FAMILY', 'ADMIN']}>
              <BurialPlotPaymentPage />
            </RoleProtectedRoute>
          } />
          <Route path="bookings/burial-plot/:bookingId" element={
            <RoleProtectedRoute allowedRoles={['FAMILY', 'ADMIN']}>
              <BurialPlotBookingDetailsPage />
            </RoleProtectedRoute>
          } />
          <Route path="bookings/:bookingId" element={
            <RoleProtectedRoute allowedRoles={['FAMILY', 'ADMIN']}>
              <AmbulanceBookingDetailsPage />
            </RoleProtectedRoute>
          } />
          <Route path="bookings/:bookingId/payment" element={
            <RoleProtectedRoute allowedRoles={['FAMILY', 'ADMIN']}>
              <AmbulanceBookingPaymentPage />
            </RoleProtectedRoute>
          } />
          <Route path="providers" element={<ProvidersPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="wallet" element={<WalletPage />} />
          <Route path="family-tree" element={<FamilyTreePage />} />
          <Route path="will" element={<MyWillPage />} />
          <Route path="cart" element={<CartPage />} />
          <Route path="checkout" element={<CheckoutPage />} />
          <Route path="my-orders" element={<MyOrdersPage />} />
          <Route path="my-orders/:id" element={<OrderDetailPage />} />

          {/* Admin routes */}
          <Route path="admin" element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <AdminDashboardPage />
            </RoleProtectedRoute>
          } />
          <Route path="admin/promo-codes" element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <PromoCodesPage />
            </RoleProtectedRoute>
          } />
          <Route path="admin/bookings" element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <BookingApprovalPage />
            </RoleProtectedRoute>
          } />
          <Route path="admin/packages" element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <PackageManagementPage />
            </RoleProtectedRoute>
          } />
          <Route path="admin/bank-accounts" element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <AccountManagementPage />
            </RoleProtectedRoute>
          } />
          <Route path="admin/payments" element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <PaymentVerificationPage />
            </RoleProtectedRoute>
          } />
          <Route path="admin/payments/verified" element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <VerifiedPaymentsPage />
            </RoleProtectedRoute>
          } />

          {/* User Management routes */}
          <Route path="admin/users" element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <UserManagementPage />
            </RoleProtectedRoute>
          } />
          <Route path="admin/users/:id" element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <UserDetailPage />
            </RoleProtectedRoute>
          } />

          {/* Finance workspace (VA Balance only) */}
          <Route path="finance" element={
            <RoleProtectedRoute allowedRoles={['FINANCE', 'ADMIN']}>
              <FinanceVABalancePage />
            </RoleProtectedRoute>
          } />
          <Route path="admin/user-documents" element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <UserDocumentsSection />
            </RoleProtectedRoute>
          } />

          {/* Infaq Admin Routes */}
          <Route path="admin/infaq/campaigns" element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <InfaqCampaignsPage />
            </RoleProtectedRoute>
          } />
          <Route path="admin/infaq/campaigns/new" element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <InfaqCampaignFormPage />
            </RoleProtectedRoute>
          } />
          <Route path="admin/infaq/campaigns/:id/edit" element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <InfaqCampaignFormPage />
            </RoleProtectedRoute>
          } />
          <Route path="admin/infaq/campaigns/:campaignId/allocate" element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <InfaqAllocateFundsFormPage />
            </RoleProtectedRoute>
          } />
          <Route path="admin/infaq/donations" element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <InfaqDonationsPage />
            </RoleProtectedRoute>
          } />
          <Route path="admin/infaq/analytics" element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <InfaqAnalyticsPage />
            </RoleProtectedRoute>
          } />
          <Route path="admin/infaq/maintenance" element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <MaintenanceInfaqApprovalPage />
            </RoleProtectedRoute>
          } />

          {/* Provider Management routes */}
          <Route path="admin/providers" element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <ProviderOverviewPage />
            </RoleProtectedRoute>
          } />
          <Route path="admin/providers/pending" element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <PendingApprovalPage />
            </RoleProtectedRoute>
          } />
          <Route path="admin/providers/pending/:providerId" element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <ProviderDetailsPage />
            </RoleProtectedRoute>
          } />
          <Route path="admin/providers/active" element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <ActiveProvidersPage />
            </RoleProtectedRoute>
          } />
          <Route path="admin/providers/active/:providerId" element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <ActiveProviderDetailsPage />
            </RoleProtectedRoute>
          } />
          <Route path="admin/providers/analytics" element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <ProviderAnalyticsPage />
            </RoleProtectedRoute>
          } />
          <Route path="admin/providers/settings" element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <ProviderSettingsPage />
            </RoleProtectedRoute>
          } />
          <Route path="admin/integrations" element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <AdminIntegrationsPage />
            </RoleProtectedRoute>
          } />
          <Route path="admin/claims" element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <InheritanceClaimsPage />
            </RoleProtectedRoute>
          } />
          <Route path="admin/claims/payouts" element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <PayoutsPage />
            </RoleProtectedRoute>
          } />
          <Route path="admin/claims/payouts/:id" element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <PayoutBatchDetailPage />
            </RoleProtectedRoute>
          } />
          <Route path="admin/claims/:id" element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <InheritanceClaimDetailPage />
            </RoleProtectedRoute>
          } />
          <Route path="admin/providers/documents" element={
            <RoleProtectedRoute allowedRoles={['ADMIN']}>
              <ProviderDocumentVerificationPage />
            </RoleProtectedRoute>
          } />

          {/* Provider routes */}
          <Route path="provider-dashboard" element={
            <RoleProtectedRoute allowedRoles={['PROVIDER', 'AMBULANCE_PROVIDER', 'USTADZ', 'CEMETERY_PROVIDER', 'VENDOR', 'OPERATOR_TRAINING', 'OPERATOR_OPERATION']}>
              <ProviderDashboardPage />
            </RoleProtectedRoute>
          } />
          <Route path="provider-dashboard/profile" element={
            <RoleProtectedRoute allowedRoles={['PROVIDER', 'AMBULANCE_PROVIDER', 'USTADZ', 'CEMETERY_PROVIDER', 'VENDOR', 'OPERATOR_TRAINING', 'OPERATOR_OPERATION']}>
              <ProviderProfilePage />
            </RoleProtectedRoute>
          } />
          <Route path="provider-dashboard/schedule" element={
            <RoleProtectedRoute allowedRoles={['PROVIDER', 'AMBULANCE_PROVIDER', 'USTADZ', 'CEMETERY_PROVIDER', 'VENDOR', 'OPERATOR_TRAINING', 'OPERATOR_OPERATION']}>
              <ProviderSchedulePage />
            </RoleProtectedRoute>
          } />
          <Route path="provider-dashboard/assets" element={
            <RoleProtectedRoute allowedRoles={['PROVIDER', 'AMBULANCE_PROVIDER', 'USTADZ', 'CEMETERY_PROVIDER', 'VENDOR', 'OPERATOR_TRAINING', 'OPERATOR_OPERATION']}>
              <AssetsRouter />
            </RoleProtectedRoute>
          } />
          <Route path="provider-dashboard/assets/:assetId" element={
            <RoleProtectedRoute allowedRoles={['PROVIDER', 'AMBULANCE_PROVIDER', 'USTADZ', 'CEMETERY_PROVIDER', 'VENDOR', 'OPERATOR_TRAINING', 'OPERATOR_OPERATION']}>
              <AssetsRouter />
            </RoleProtectedRoute>
          } />
          {/* Dedicated routes for specific provider types */}
          <Route path="provider-dashboard/assets/vehicles/:assetId" element={
            <RoleProtectedRoute allowedRoles={['AMBULANCE_PROVIDER']}>
              <AmbulanceAssetManagementPage />
            </RoleProtectedRoute>
          } />
          <Route path="provider-dashboard/assets/burial-plots/:assetId" element={
            <RoleProtectedRoute allowedRoles={['CEMETERY_PROVIDER']}>
              <CemeteryAssetManagementPage />
            </RoleProtectedRoute>
          } />
          <Route path="provider-dashboard/bookings" element={
            <RoleProtectedRoute allowedRoles={['PROVIDER', 'AMBULANCE_PROVIDER', 'USTADZ', 'CEMETERY_PROVIDER', 'VENDOR', 'OPERATOR_TRAINING', 'OPERATOR_OPERATION']}>
              <ProviderBookingsPage />
            </RoleProtectedRoute>
          } />
          <Route path="provider-dashboard/infaq" element={
            <RoleProtectedRoute allowedRoles={['PROVIDER', 'AMBULANCE_PROVIDER', 'CEMETERY_PROVIDER', 'VENDOR', 'OPERATOR_TRAINING', 'OPERATOR_OPERATION']}>
              <ProviderInfaqPage />
            </RoleProtectedRoute>
          } />
          <Route path="provider-dashboard/infaq/:id" element={
            <RoleProtectedRoute allowedRoles={['PROVIDER', 'AMBULANCE_PROVIDER', 'CEMETERY_PROVIDER', 'VENDOR', 'OPERATOR_TRAINING', 'OPERATOR_OPERATION']}>
              <MaintenanceInfaqDetailPage />
            </RoleProtectedRoute>
          } />

          {/* Vendor routes */}
          <Route path="vendor-dashboard" element={
            <RoleProtectedRoute allowedRoles={['VENDOR']}>
              <SouvenirVendorDashboard />
            </RoleProtectedRoute>
          } />
          <Route path="vendor-dashboard/products" element={
            <RoleProtectedRoute allowedRoles={['VENDOR']}>
              <VendorProductsPage />
            </RoleProtectedRoute>
          } />
          <Route path="vendor-dashboard/orders" element={
            <RoleProtectedRoute allowedRoles={['VENDOR']}>
              <VendorOrdersPage />
            </RoleProtectedRoute>
          } />
          <Route path="vendor-dashboard/profile" element={
            <RoleProtectedRoute allowedRoles={['VENDOR']}>
              <VendorProfilePage />
            </RoleProtectedRoute>
          } />
          <Route path="vendor/products/new" element={
            <RoleProtectedRoute allowedRoles={['VENDOR']}>
              <NewProductPage />
            </RoleProtectedRoute>
          } />
          <Route path="vendor/products/:id" element={
            <RoleProtectedRoute allowedRoles={['VENDOR']}>
              <ProductDetailPage />
            </RoleProtectedRoute>
          } />
          <Route path="vendor/products/:id/edit" element={
            <RoleProtectedRoute allowedRoles={['VENDOR']}>
              <EditProductPage />
            </RoleProtectedRoute>
          } />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </div>
  )
}

export default App